
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'
import { extractTextFromHtml } from '@/utils/articleContentHelper'

export default defineComponent({
  name: 'StarContentAudio',
  props: {
    mediaFile: {
      type: String,
      default: '',
    },
    textContent: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const audioRef = ref<HTMLAudioElement | null>(null)
    const isPaused = ref(true)
    const playbackSpeed = ref(1.0)
    const plainText = ref('')
    const utterance = ref<SpeechSynthesisUtterance | null>(null)
    const isEnd = ref(false)

    const resetTts = () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel()
      }
      utterance.value = null
      isPaused.value = true
      isEnd.value = false
    }

    const resetAudio = () => {
      const audio = audioRef.value
      if (!audio) return
      if (!audio.paused) {
        audio.pause()
      }
      audio.currentTime = 0
      playbackSpeed.value = 1.0
      audio.playbackRate = 1.0
    }

    const syncPlainText = () => {
      plainText.value = extractTextFromHtml(props.textContent)
    }

    const speakText = (text: string) => {
      if (!text || !('speechSynthesis' in window)) return
      window.speechSynthesis.cancel()
      const nextUtterance = new SpeechSynthesisUtterance(text)
      nextUtterance.lang = 'zh-CN'
      nextUtterance.onend = () => {
        isPaused.value = true
        isEnd.value = true
        window.speechSynthesis.cancel()
      }
      utterance.value = nextUtterance
      window.speechSynthesis.speak(nextUtterance)
      isPaused.value = false
      isEnd.value = false
    }

    const onToggleTts = () => {
      if (isPaused.value) {
        if (isEnd.value || !utterance.value) {
          speakText(plainText.value)
        } else {
          window.speechSynthesis.resume()
          isPaused.value = false
        }
      } else {
        window.speechSynthesis.pause()
        isPaused.value = true
      }
    }

    const seekForward = () => {
      const audio = audioRef.value
      if (audio) audio.currentTime += 15
    }

    const seekBackward = () => {
      const audio = audioRef.value
      if (audio) audio.currentTime = Math.max(0, audio.currentTime - 15)
    }

    const togglePlaybackSpeed = () => {
      playbackSpeed.value = playbackSpeed.value === 1.0 ? 1.5 : 1.0
      const audio = audioRef.value
      if (audio) audio.playbackRate = playbackSpeed.value
    }

    const bindAudioEvents = () => {
      const audio = audioRef.value
      if (!audio) return
      audio.onpause = () => {
        isPaused.value = true
      }
      audio.onplay = () => {
        isPaused.value = false
      }
    }

    watch(
      () => props.textContent,
      () => {
        resetTts()
        syncPlainText()
      },
      { immediate: true }
    )

    watch(
      () => props.mediaFile,
      () => {
        resetTts()
        resetAudio()
      }
    )

    onMounted(() => {
      syncPlainText()
      nextTick(() => bindAudioEvents())
    })

    onBeforeUnmount(() => {
      resetTts()
      resetAudio()
    })

    return {
      audioRef,
      isPaused,
      playbackSpeed,
      plainText,
      onToggleTts,
      seekForward,
      seekBackward,
      togglePlaybackSpeed,
    }
  },
})
